import React, { useEffect, useState } from "react"

type PaymentSource = "paypal" | "card" | "apple_pay" | "google-pay"

interface ButtonProps {
  createOrder: (data: any, source: PaymentSource) => Promise<any>
  onApprove: (data: any, source: PaymentSource) => Promise<any>
  createVaultSetupToken?: (data: any, source: PaymentSource) => Promise<any>
  isSdkReady: boolean
  // Service Checkout
  reactiveOrderTotal?: number
  // Subscription Checkout
  subscriptionAmount?: number
  subscriptionPromo?: number
}

const PayPalApplePay: React.FC<ButtonProps> = ({
  createOrder,
  onApprove,
  isSdkReady,
  reactiveOrderTotal,
  subscriptionAmount,
  subscriptionPromo,
}) => {
  const [isAppleReady, setIsAppleReady] = useState(false)
  const [total, setTotal] = useState(0)

  useEffect(() => {
    if (reactiveOrderTotal != null) {
      setTotal(reactiveOrderTotal)
    }
  }, [reactiveOrderTotal])

  useEffect(() => {
    if (subscriptionAmount == null && subscriptionPromo == null) return

    const val = Number(subscriptionAmount ?? 0)
    const promo = Number(subscriptionPromo ?? 0)
    setTotal(val - promo)
  }, [subscriptionAmount, subscriptionPromo])

  const setupApplePay = async () => {
    const paypal = (window as any).paypal

    if (!paypal?.Applepay) {
      console.warn("PayPal Apple Pay not available")
      return
    }

    const applepay = paypal.Applepay()
    const config = await applepay.config()

    if (!config?.isEligible) {
      console.warn("Apple Pay not eligible")
      return
    }

    const container = document.getElementById("applepay-container")
    if (!container) return

    container.innerHTML = ""

    const btn = document.createElement("apple-pay-button")
    btn.setAttribute("buttonstyle", "black")
    btn.setAttribute("type", "buy")
    btn.style.width = "100%"
    btn.style.height = "48px"
    container.appendChild(btn)

    btn.onclick = () => {
      const paymentRequest = {
        countryCode: config.countryCode,
        currencyCode: "USD",
        merchantCapabilities: config.merchantCapabilities,
        supportedNetworks: config.supportedNetworks,
        total: {
          label: "Zoee",
          amount: total.toFixed(2),
          type: "final",
        },
        requiredBillingContactFields: ["postalAddress", "name", "email"],
      }

      const session = new window.ApplePaySession(4, paymentRequest)

      session.onvalidatemerchant = (event: any) => {
        applepay
          .validateMerchant({ validationUrl: event.validationURL })
          .then((res: any) =>
            session.completeMerchantValidation(res.merchantSession)
          )
          .catch(() => session.abort())
      }

      session.onpaymentauthorized = async (event: any) => {
        try {
          const orderId = await createOrder({}, "apple_pay")

          await applepay.confirmOrder({
            orderId,
            token: event.payment.token,
            billingContact: event.payment.billingContact,
          })

          await onApprove({ orderID: orderId }, "apple_pay")

          session.completePayment({
            status: window.ApplePaySession.STATUS_SUCCESS,
          })
        } catch {
          session.completePayment({
            status: window.ApplePaySession.STATUS_FAILURE,
          })
        }
      }

      session.begin()
    }
  }

  useEffect(() => {
    if (!isSdkReady) return
    if (!document.getElementById("apple-pay-sdk")) {
      const script = document.createElement("script")
      script.id = "apple-pay-sdk"
      script.src = "https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js"
      script.async = true
      script.onload = () => setIsAppleReady(true)
      document.body.appendChild(script)
    } else {
      setIsAppleReady(true)
    }
  }, [isSdkReady])

  useEffect(() => {
    if (!isAppleReady || !window.ApplePaySession) return

    const container = document.getElementById("applepay-container")
    if (container) container.innerHTML = ""
    setupApplePay()
  }, [isAppleReady, total])

  return <div id="applepay-container" />
}

export default PayPalApplePay
